/**
 * CampaignDayDelayEditPure
 *
 * Inline editor that swaps in for `CampaignDayDelayPure`'s "wait N days"
 * pill when the user clicks it. Same visual chrome as the wait band so
 * the connector area doesn't shift when transitioning view → edit:
 *
 *   - centered vertical spine threads through the row (matches the wait
 *     band's spine)
 *   - inline number input + Update button + Cancel button on a single
 *     centered row, each rendered as a pill so the row reads as a unit
 *
 * Pure / display-only — receives the current delay in days, an
 * `onSubmit(newDays)` callback, and an `onCancel` callback.
 */
import type { ComponentProps } from 'react';
import { useState } from 'react';
import { cn } from '../cn';
import { Button, ButtonSegment, Input } from '../Input2';

type DelayUnit = 'days' | 'weeks';

export type CampaignDayDelayEditPureProps = Omit<ComponentProps<'div'>, 'children' | 'onSubmit'> & {
  /** Current wait length in whole days (used to seed the input). */
  initialDays: number;
  /** When true, seed the editor in "weeks" mode (calendar-week aligned). */
  initialAlignWeek?: boolean;
  /** True when the delay this is editing diverges from the template. */
  isDifferentFromTemplate: boolean | undefined;
  /**
   * Called with the new wait length (in whole days) and whether it's calendar-week
   * aligned when the user hits Update. In weeks mode `days` is `value * 7`.
   */
  onSubmit: (days: number, alignWeek: boolean) => void | Promise<void>;
  /** Called when the user hits Cancel (or escapes). */
  onCancel: () => void;
};

export function CampaignDayDelayEditPure({
  initialDays,
  initialAlignWeek = false,
  isDifferentFromTemplate,
  onSubmit,
  onCancel,
  className,
  ...divProps
}: CampaignDayDelayEditPureProps) {
  const [unit, setUnit] = useState<DelayUnit>(initialAlignWeek ? 'weeks' : 'days');
  const [value, setValue] = useState(`${initialAlignWeek ? Math.max(1, Math.round(initialDays / 7)) : initialDays}`);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const parsed = Number.parseInt(value, 10);
    // Weeks are calendar-aligned and start at 1; days allow 0 (immediate / first step).
    if (Number.isNaN(parsed) || parsed < (unit === 'weeks' ? 1 : 0)) return;
    void onSubmit(unit === 'weeks' ? parsed * 7 : parsed, unit === 'weeks');
  };

  return (
    <div
      {...divProps}
      data-testid="campaign-day-delay-edit"
      className={cn('relative flex flex-col', className)}>
      {/* Centered spine matches the wait band's spine so the column
          stays visually continuous when toggling view → edit. */}
      <span
        aria-hidden
        className={cn(
          'pointer-events-none absolute left-1/2 top-0 h-full w-px -translate-x-1/2',
          isDifferentFromTemplate
            ? 'bg-purple-300 dark:bg-purple-700'
            : 'bg-lynch-200 dark:bg-slate-700',
        )}
      />
      <div className="relative z-10 my-2 flex w-full items-center justify-center px-2">
        <form
          onSubmit={handleSubmit}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              e.preventDefault();
              onCancel();
            }
          }}
          className={cn(
            'inline-flex items-center gap-2 rounded-lg border-2 border-dashed px-3 py-2 shadow-xs',
            isDifferentFromTemplate
              ? 'border-purple-300 bg-purple-50/60 dark:border-purple-700 dark:bg-purple-950/30'
              : 'border-lynch-300 bg-lynch-50/60 dark:border-slate-600 dark:bg-slate-800/40',
          )}>
          <span className="text-xs font-medium text-lynch-700 dark:text-lynch-300">wait</span>
          <Input
            data-testid="campaign-day-delay-edit-input"
            type="number"
            min={unit === 'weeks' ? 1 : 0}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            autoFocus
            size="xs"
            className="w-16"
          />
          <ButtonSegment
            data-testid="campaign-day-delay-edit-unit"
            size="xs"
            selectedId={unit}
            onChange={(u) => setUnit(u)}>
            <Button id="days" data-testid="campaign-day-delay-edit-unit-days">days</Button>
            <Button id="weeks" data-testid="campaign-day-delay-edit-unit-weeks">weeks</Button>
          </ButtonSegment>
          <Button
            data-testid="campaign-day-delay-edit-submit"
            type="submit"
            size="xs"
            color="blue">
            Update
          </Button>
          <Button
            data-testid="campaign-day-delay-edit-cancel"
            type="button"
            size="xs"
            color="gray"
            text
            onClick={onCancel}>
            Cancel
          </Button>
        </form>
      </div>
    </div>
  );
}
